import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import exteriorIcon from "../../../assets/icons/exterior.svg";
import { useToast } from "../../../contexts/ToastContext";
import confirmModal from "../../../components/custom_antd/confirmModal";
import finalTouchesIcon from "../../../assets/icons/finalTouches.svg";
import inspectionIcon from "../../../assets/icons/inspection.svg";
import interiorIcon from "../../../assets/icons/interior.svg";
import apiClient from "../../../helpers/apiClient";
import CheckList from "./CheckList";

const pageStyle = {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0%",
    minHeight: 0,
};

const contentStyle = {
    display: "flex",
    minHeight: 0,
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
};

// Overview
const overviewStyle = {
    backgroundColor: "#fff",
    borderRadius: "0.8rem",
    padding: "0.8rem 1.8rem 1.5rem 1.8rem",
};

const overviewHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1.5rem",
};

const overviewTitleStyle = {
    fontSize: "1.8rem",
    fontWeight: "600",
    color: "#5D5DF6",
};

const buttonStyle = {
    padding: "0.5rem 1.2rem",
    borderRadius: "33px",
    border: "none",
    cursor: "pointer",
    fontWeight: "bold",
    marginLeft: "1rem",
};

const deleteButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#E82222",
    color: "white",
};

// const updateButtonStyle = {
//     ...buttonStyle,
//     backgroundColor: "#5D5DF6",
//     color: "white",
// };

const overviewContentStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "1rem",
    padding: "0px 4rem",
};

const overviewItemStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
};

const overviewLabelStyle = {
    fontSize: "0.9rem",
    color: "#9B9BAA",
    fontWeight: "400",
    marginBottom: "0.2rem",
    whiteSpace: "nowrap",
};

const overviewValueStyle = {
    fontSize: "1rem",
    color: "#333333",
    fontWeight: "600",
    whiteSpace: "nowrap",
};

const dividerSection = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "1.5rem",
};

const divider = {
    height: "2px",
    width: "2rem",
    backgroundColor: "#9B9BAA",
    transform: "rotate(90deg)",
};

// Tab
const tabContainerStyle = {
    marginTop: "1.5rem",
    display: "flex",
    flexDirection: "column",
    gap: "1.5rem",
    overflow: "hidden",
};

const tabHeaderStyle = {
    display: "flex",
    padding: "1rem",
    backgroundColor: "#fff",
    borderRadius: "0.8rem",
};

const tabStyle = {
    padding: "10px 35px 10px 35px",
    cursor: "pointer",
    fontWeight: "600",
    color: "#05122E",
};

const activeTabStyle = {
    ...tabStyle,
    color: "#fff",
    backgroundColor: "#5D5DF6",
    borderRadius: "10px",
};

const tabContentStyle = {
    padding: "1rem",
    backgroundColor: "#fff",
    borderRadius: "0.8rem",
    overflowY: "scroll",
};

// const btnDisabled = {
//     cursor: "not-allowed",
//     opacity: "0.6",
// };

// const btnHover = {
//     backgroundColor: "#4A4AE8",
// };

const CleanerStatusDetails = () => {
    const { id } = useParams();
    const [activeTab, setActiveTab] = useState("Interior");
    const [statusDetails, setStatusDetails] = useState({});
    const [error, setError] = useState(null);
    const history = useHistory();
    const { t } = useTranslation("cleaner");
    const toast = useToast();

    const fetchCleanerStatus = useCallback(async () => {
        setError(null);
        try {
            const response = await apiClient.get(`/api/cleaner-status/${id}`);
            // console.log(response?.data);
            setStatusDetails(response?.data?.data);
        } catch (err) {
            // console.error("Error fetching cleaner status: ", err);
            setError(`Failed to fetch cleaner status: ${err.message}`);
        }
    }, [id]);

    useEffect(() => {
        fetchCleanerStatus();
    }, [fetchCleanerStatus]);

    const handleUpdateStatus = async (section, itemName, isComplete) => {
        try {
            const updatedValues = { ...statusDetails.values };
            updatedValues[section][itemName] = isComplete;

            const response = await apiClient.patch(`/api/cleaner-status/${id}`, {
                values: updatedValues,
            });

            if (response.status === 200) {
                setStatusDetails((prevState) => ({
                    ...prevState,
                    values: updatedValues,
                }));
            }
        } catch (err) {
            // console.error("Error updating cleaner status: ", err);
            setError(`Failed to update cleaner status: ${err.message}`);
        }
    };

    const handleDeleteStatus = async (statusId) => {
        try {
            const confirmed = await confirmModal({
                title: t("confirmDeleteTitle"),
                okText: t("confirm"),
                cancelText: t("cancel"),
            });

            if (confirmed) {
                const response = await apiClient.delete(`/api/cleaner-status/${statusId}`);

                if (response.status === 200) {
                    toast.success({
                        message: t("deletedTitle"),
                        description: t("deletedDescription"),
                    });
                    history.push("/cleaner-status");
                }
            }
        } catch (err) {
            // console.error("Error deleting cleaner status: ", err);
            setError(`Failed to delete cleaner status: ${err.message}`);
            toast.error({
                message: t("deleteFailedTitle"),
                description: t("deleteFailedDescription", { message: err.message }),
            });
        }
    };

    if (error) {
        return <div>Error: {error}</div>;
    }

    const { date, carPlateNumber, cleanerData, values } = statusDetails;

    const renderTabContent = () => {
        switch (activeTab) {
            case "Interior":
                return (
                    <CheckList
                        title="Interior"
                        data={values?.interior}
                        icon={interiorIcon}
                        onUpdate={(itemName, isComplete) => handleUpdateStatus("interior", itemName, isComplete)}
                    />
                );
            case "Exterior":
                return (
                    <CheckList
                        title="Exterior"
                        data={values?.exterior}
                        icon={exteriorIcon}
                        onUpdate={(itemName, isComplete) => handleUpdateStatus("exterior", itemName, isComplete)}
                    />
                );
            case "Inspection":
                return (
                    <CheckList
                        fetchCleanerStatus={fetchCleanerStatus}
                        title="Inspection"
                        data={values?.inspection}
                        allData={statusDetails}
                        icon={inspectionIcon}
                        onUpdate={(itemName, isComplete) => handleUpdateStatus("inspection", itemName, isComplete)}
                    />
                );
            case "Final Touch":
                return (
                    <CheckList
                        title="Final Touch"
                        data={values?.finalTouches}
                        icon={finalTouchesIcon}
                        onUpdate={(itemName, isComplete) => handleUpdateStatus("finalTouches", itemName, isComplete)}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div style={pageStyle}>
            <div style={contentStyle}>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    {/* Overview Section */}
                    <div style={overviewStyle}>
                        <div style={overviewHeaderStyle}>
                            <h2 style={overviewTitleStyle}>Overview</h2>
                            <div>
                                <button onClick={() => handleDeleteStatus(id)} style={deleteButtonStyle}>
                                    Delete
                                </button>
                            </div>
                        </div>
                        <div style={overviewContentStyle}>
                            <div style={dividerSection}>
                                <div style={overviewItemStyle}>
                                    <span style={overviewLabelStyle}>ID</span>
                                    <span style={overviewValueStyle}>{id}</span>
                                </div>
                                <div style={divider}></div>
                            </div>
                            <div style={dividerSection}>
                                <div style={overviewItemStyle}>
                                    <span style={overviewLabelStyle}>Cleaner ID</span>
                                    <span style={overviewValueStyle}>{cleanerData?.cleanerID || "N/A"}</span>
                                </div>
                                <div style={divider}></div>
                            </div>
                            <div style={dividerSection}>
                                <div style={overviewItemStyle}>
                                    <span style={overviewLabelStyle}>Plate Number</span>
                                    <span style={overviewValueStyle}>{carPlateNumber || "N/A"}</span>
                                </div>
                                <div style={divider}></div>
                            </div>
                            <div style={dividerSection}>
                                <div style={overviewItemStyle}>
                                    <span style={overviewLabelStyle}>Cleaner Name</span>
                                    <span style={overviewValueStyle}>{cleanerData?.Name || "N/A"}</span>
                                </div>
                                <div style={divider}></div>
                            </div>
                            <div>
                                <div style={overviewItemStyle}>
                                    <span style={overviewLabelStyle}>Date & Time</span>
                                    <span style={overviewValueStyle}>
                                        {date ? new Date(date).toLocaleString() : "N/A"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tab Section */}
                    <div style={tabContainerStyle}>
                        <div style={tabHeaderStyle}>
                            {["Interior", "Exterior", "Inspection", "Final Touch"]?.map((tab) => (
                                <div
                                    key={tab}
                                    style={activeTab === tab ? activeTabStyle : tabStyle}
                                    onClick={() => setActiveTab(tab)}>
                                    {tab}
                                </div>
                            ))}
                        </div>
                        <div style={tabContentStyle}>{renderTabContent()}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CleanerStatusDetails;
